import {DateTime} from 'luxon';

// Format constants
// ---------------------------------------------------------------

// https://github.com/moment/luxon/blob/master/docs/formatting.md#table-of-tokens

export const DEFAULT_TIME_ZONE: string = 'UTC';

// October 7, 2026
export const DATE_LONG_FORMAT: string = 'MMMM d, yyyy';

// Oct 7, 2026
export const DATE_MED_FORMAT: string = 'MMM d, yyyy';

// 2026-10-07
export const DATE_ISO_FORMAT: string = 'yyyy-MM-dd';

// 13:35
export const TIME_FORMAT: string = 'HH:mm';

// 13:35:07
export const TIME_FORMAT_WITH_SECONDS: string = 'HH:mm:ss';

// Utility functions
// ---------------------------------------------------------------

type FormatDateOptions = {
  withTime?: boolean;
  withZone?: boolean;
  withSeconds?: boolean;
};

const formatDate = (
  date?: string | null,
  baseFormat?: string,
  {
    withTime = false,
    withZone = false,
    withSeconds = false,
  }: FormatDateOptions = {},
): string => {
  const parts: string[] = baseFormat ? [baseFormat] : [];

  if (withTime) {
    if (baseFormat) {
      parts.push('-'); // Separator when there's date + time together
    }

    parts.push(withSeconds ? TIME_FORMAT_WITH_SECONDS : TIME_FORMAT);

    if (withZone) {
      parts.push('ZZZZ');
    }
  }

  return date
    ? DateTime.fromISO(date, {zone: DEFAULT_TIME_ZONE}).toFormat(
        parts.join(' '),
      )
    : '';
};

export const formatDateLong = (
  date?: string | null,
  options: FormatDateOptions = {},
): string => formatDate(date, DATE_LONG_FORMAT, options);

export const formatDateMed = (
  date?: string | null,
  options: FormatDateOptions = {},
): string => formatDate(date, DATE_MED_FORMAT, options);

export const formatDateISO = (date?: string | null): string =>
  formatDate(date, DATE_ISO_FORMAT);

export const formatTime = (
  date?: string | null,
  options: Omit<FormatDateOptions, 'withTime'> = {},
): string => formatDate(date, undefined, {withTime: true, ...options});

export const formatDateRelative = (date?: string | null): string | null =>
  date ? DateTime.fromISO(date, {zone: DEFAULT_TIME_ZONE}).toRelative() : null;

export const formatDateRange = (
  range: string[],
  separator: string = '–', // typographically correct en dash
  format: string = DATE_ISO_FORMAT,
): string => {
  return range
    .map(date =>
      DateTime.fromISO(date, {zone: DEFAULT_TIME_ZONE}).toFormat(format),
    )
    .join(` ${separator} `);
};

export const isSingleDayDateRange = (
  from: DateTime | null,
  to: DateTime | null,
) => {
  if (!from || !to) {
    return false;
  }

  return (
    from.hasSame(to, 'day') &&
    from.equals(from.startOf('day')) &&
    to.equals(to.endOf('day'))
  );
};
